import {
  Button,
  Callout,
  FormField,
  Modal,
  TextArea,
  TextInput,
} from '@dev-spendesk/grapes';
import cx from 'classnames';
import { type FormikErrors, type FormikProps, getIn, useFormik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import { useIsAnalyticalSplitActivated } from 'modules/bookkeep/apis/analytical-split/useAnalyticalSplitStatus';
import { useAccountingSoftwareName } from 'src/core/modules/accounting-integration/apis';
import { type AnalyticalFieldExportSettings } from 'src/core/modules/accounting-integration/apis/useAnalyticalFieldExportSettings';
import { type SyncMode } from 'src/core/modules/accounting-integration/apis/useSetSyncSettings';
import { AnalyticalFieldValueMappingSelector } from 'src/core/modules/accounting-integration/components/AnalyticalFieldMapping/AnalyticalFieldValueMappingSelector';
import {
  type AnalyticalFieldValueMappingV2,
  type AnalyticalFieldMappingV2,
} from 'src/core/modules/bookkeep';
import { EcFormModalContentPullMode } from 'src/core/modules/company/expense-categories/components/EcFormModal/EcFormModalContentPullMode';
import { EcFormModalContentWithSync } from 'src/core/modules/company/expense-categories/components/EcFormModal/EcFormModalContentWithSync';

import {
  MAX_CHAR_PER_CUSTOM_FIELDS,
  MAX_VALUES_PER_CUSTOM_FIELD,
  validate,
} from './validate';
import { type CustomFieldCreationForm } from '../../containers/CustomFieldCreationModalContainer';
import { convertValues } from '../../utils/convertValues';
import { removeNullValues } from '../../utils/removeNullValues';
import { CustomFieldInfoStep } from '../CustomFieldModalInfo/CustomFieldModalInfo';

import './CustomFieldCreationModal.css';

type CreationSteps = 'sync' | 'pull' | 'pullConfirm' | 'info' | 'values';

export type SyncErrors = 'missingSyncMode' | 'missingSelectedField';

type Props = {
  isOpen: boolean;
  isMapSyncEnabled: boolean;
  syncMode: SyncMode | undefined;
  setSyncMode: (mode: SyncMode | undefined) => void;
  error: SyncErrors | null;
  setError: (error: SyncErrors | null) => void;
  exportSettings: AnalyticalFieldExportSettings | undefined;
  setExportSettings: (settings: AnalyticalFieldExportSettings) => void;
  analyticalFieldMappings: AnalyticalFieldMappingV2[];
  fieldMapping: AnalyticalFieldMappingV2 | undefined;
  setFieldMapping: (mapping: AnalyticalFieldMappingV2 | undefined) => void;
  onClose(): void;
  onSubmit(values: CustomFieldCreationForm): void;
  valueMapping?: AnalyticalFieldValueMappingV2;
  setValueMapping: React.Dispatch<
    React.SetStateAction<AnalyticalFieldValueMappingV2 | undefined>
  >;
};

export const CustomFieldCreationModal = ({
  isOpen,
  isMapSyncEnabled,
  syncMode,
  setSyncMode,
  error,
  setError,
  exportSettings,
  setExportSettings,
  analyticalFieldMappings,
  fieldMapping,
  setFieldMapping,
  onClose,
  onSubmit,
  valueMapping,
  setValueMapping,
}: Props) => {
  const { t } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();
  const isAnalyticalSplitActivated = useIsAnalyticalSplitActivated();

  const [step, setStep] = useState<CreationSteps>(
    isMapSyncEnabled ? 'sync' : 'info',
  );

  const formikPropsInfo = useFormik<CustomFieldCreationForm>({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      name: '',
      type: 'mandatory',
      isSplittable: isAnalyticalSplitActivated,
    },
    validate: (values) => {
      const errors: FormikErrors<CustomFieldCreationForm> = {};
      if (isEmpty(values.name)) {
        errors.name = t('customFields.creation.nameRequired');
      }
      return errors;
    },
    onSubmit: () => {
      if (syncMode === 'pull') {
        setStep('pull');
        return;
      }
      setStep('values');
    },
  });

  const formikPropsValues = useFormik<Parameters<typeof validate>[0]>({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      customFieldValues: undefined,
    },
    validate: (values: { customFieldValues?: string }) => {
      return validate(values);
    },
    onSubmit: async (values) => {
      await onSubmit({
        ...formikPropsInfo.values,
        values: values.customFieldValues,
      });
    },
  });

  useEffect(() => {
    // clear input manually as the modal is "always opened" due to the isOpen state
    formikPropsInfo.resetForm();
    formikPropsValues.resetForm();

    setSyncMode(undefined);
    setError(null);

    if (!isMapSyncEnabled) {
      setStep('info');
    }

    if (isMapSyncEnabled) {
      if (!isOpen) {
        setTimeout(() => {
          setStep('sync');
        }, 1000);
        return;
      }

      setStep('sync');
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (step === 'info') {
      formikPropsInfo.handleSubmit();
    }

    if (step === 'values') {
      formikPropsValues.handleSubmit();
    }

    if (step === 'sync') {
      if (!syncMode) {
        setError('missingSyncMode');
        return;
      }

      setStep('info');
    }

    if (step === 'pull') {
      formikPropsValues.handleSubmit();
      setStep('pullConfirm');
    }

    if (step === 'pullConfirm') {
      onClose();
    }
  };

  const handleBack = () => {
    if (step === 'pull') {
      setStep('sync');
    }

    if (step === 'info' && isMapSyncEnabled) {
      setStep('sync');
    }

    if (step === 'values') {
      setStep('info');
    }

    if (step === 'pull') {
      setStep('info');
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const getActionButtons = () => {
    if (step === 'sync') {
      return [
        <Button
          key="cancel"
          variant="secondaryNeutral"
          text={t('expenseCategories.form.cancel')}
          onClick={handleCancel}
        />,
        <Button
          key="save"
          variant="primaryBrand"
          text={t('misc.continue')}
          onClick={handleSubmit}
        />,
      ];
    }

    if (step === 'info') {
      const isBackButtonVisible = isMapSyncEnabled; // TODO

      return [
        <Button
          key="cancel"
          variant="secondaryNeutral"
          iconName={isBackButtonVisible ? 'chevron-left' : undefined}
          text={
            isBackButtonVisible
              ? t('misc.back')
              : t('expenseCategories.form.cancel')
          }
          onClick={() => (isBackButtonVisible ? handleBack() : handleCancel())}
        />,
        <Button
          key="save"
          variant="primaryBrand"
          text={t('misc.continue')}
          onClick={handleSubmit}
        />,
      ];
    }

    if (step === 'pull') {
      return [
        <Button
          key="cancel"
          variant="secondaryNeutral"
          iconName="chevron-left"
          text={t('misc.back')}
          onClick={handleBack}
        />,
        <Button
          key="save"
          variant="primaryBrand"
          text={t('expenseCategories.mapSync.startPull')}
          onClick={handleSubmit}
        />,
      ];
    }

    if (step === 'pullConfirm') {
      return [
        <Button
          key="save"
          variant="primaryBrand"
          text={t('expenseCategories.mapSync.gotIt')}
          onClick={handleSubmit}
        />,
      ];
    }

    return [
      <Button
        text={t('misc.back')}
        key="back-button"
        iconName="chevron-left"
        iconPosition="left"
        variant="secondaryNeutral"
        onClick={() => handleBack()}
      />,
      <Button
        key="submit-button"
        text={t('customFields.creation.submit')}
        isDisabled={formikPropsValues.isSubmitting}
        onClick={() => handleSubmit()}
      />,
    ];
  };

  const getModalTitle = (): string => {
    switch (step) {
      case 'values':
      case 'info':
        return t('customFields.creation.title');
      case 'sync':
        return t('customFields.mapSync.syncTitle');
      case 'pull':
        return t('expenseCategories.mapSync.pullTitleCustomField', {
          integration: accountingSoftwareName,
        });
      case 'pullConfirm':
        return t('expenseCategories.mapSync.pullConfirmTitle', {
          integration: accountingSoftwareName,
        });
      default:
        return '';
    }
  };

  const getModalSubtitle = (): string => {
    switch (step) {
      case 'pull':
        return t('expenseCategories.mapSync.pullSubtitle', {
          integration: accountingSoftwareName,
        });
      case 'pullConfirm':
        return t('expenseCategories.mapSync.pullConfirmSubtitle', {
          integration: accountingSoftwareName,
        });
      default:
        return '';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title={getModalTitle()}
      subtitle={getModalSubtitle()}
      iconName={step === 'pullConfirm' ? 'arrow-path' : 'circle-plus'}
      actions={getActionButtons()}
    >
      {step === 'info' && <CustomFieldInfoStep formikProps={formikPropsInfo} />}

      {step === 'values' && (
        <CustomFieldValuesStep
          formikProps={formikPropsValues}
          isMapSyncEnabled={isMapSyncEnabled}
          fieldMapping={fieldMapping}
          syncMode={syncMode}
          valueMapping={valueMapping}
          setValueMapping={setValueMapping}
        />
      )}

      {step === 'sync' && (
        <EcFormModalContentWithSync
          syncMode={syncMode}
          onChange={(syncModeToSet) => {
            setError(null);
            if (syncModeToSet) {
              setSyncMode(syncModeToSet);
            }
          }}
        />
      )}

      {step === 'pull' && (
        <div className="mt-24">
          <EcFormModalContentPullMode
            exportSettings={exportSettings}
            isSplittable={!!formikPropsInfo.values.isSplittable}
            onExportSettingsChange={(settings) => setExportSettings(settings)}
            fieldMapping={fieldMapping}
            analyticalFieldMappings={analyticalFieldMappings}
            onChange={setFieldMapping}
            syncMode={syncMode}
            fieldName={formikPropsInfo.values.name}
          />
        </div>
      )}

      {error && (
        <Callout
          className="mt-16"
          variant="alert"
          title={t('expenseCategories.mapSync.missingSyncMode')}
        />
      )}
    </Modal>
  );
};

export const CustomFieldValuesStep = ({
  formikProps,
  existingValuesCount = 0,
  isMapSyncEnabled,
  fieldMapping,
  syncMode,
  valueMapping,
  setValueMapping,
}: {
  formikProps: FormikProps<{ customFieldValues?: string }>;
  existingValuesCount?: number;
  isMapSyncEnabled: boolean;
  syncMode?: SyncMode;
  fieldMapping: AnalyticalFieldMappingV2 | undefined;
  valueMapping?: AnalyticalFieldValueMappingV2;
  setValueMapping: React.Dispatch<
    React.SetStateAction<AnalyticalFieldValueMappingV2 | undefined>
  >;
}) => {
  const { t } = useTranslation('global');

  const customFieldValues = formikProps.values.customFieldValues ?? '';
  const customFieldValuesWithoutNulls = removeNullValues(customFieldValues);

  const maxValuesPerCustomField =
    MAX_VALUES_PER_CUSTOM_FIELD - existingValuesCount;
  const currentNumberOfValues =
    convertValues(formikProps.values.customFieldValues)?.length ?? 0;

  const valuesErrors: ReturnType<typeof validate> =
    getIn(formikProps, 'errors') ?? {};

  if (isMapSyncEnabled && fieldMapping && fieldMapping.internalId) {
    return (
      <div className="mt-24">
        <FormField
          className="CustomFieldCreationModal__values"
          label={t('customFields.creation.valuesLabel')}
        >
          <TextInput
            value={customFieldValuesWithoutNulls}
            placeholder={t('customFields.settings.modals.edition.placeholder')}
            onChange={(event) => {
              formikProps.setFieldValue(
                'customFieldValues',
                event.target.value,
              );
            }}
          />
        </FormField>

        <AnalyticalFieldValueMappingSelector
          selectedValue={
            valueMapping && {
              ...valueMapping,
              label: valueMapping.externalName,
              key: valueMapping.id,
            }
          }
          values={fieldMapping.values}
          onValueSelected={(value) => {
            setValueMapping(value);
          }}
          syncMode={syncMode}
        />
      </div>
    );
  }

  return (
    <div>
      <FormField
        className="CustomFieldCreationModal__values"
        label={t('customFields.creation.valuesLabel')}
      >
        <>
          <TextArea
            placeholder={t('customFields.creation.valuesPlaceholder')}
            value={customFieldValuesWithoutNulls}
            onChange={(e) =>
              formikProps.setFieldValue('customFieldValues', e.target.value)
            }
            rows={7}
            isInvalid={!isEmpty(valuesErrors)}
          />
          <span
            className={cx('CustomFieldCreationModal__values__counter body-s', {
              CustomFieldCreationModal__values__counter__tooMany:
                valuesErrors.oneCustomFieldValueRequired ||
                valuesErrors.tooManyCustomFieldValues,
            })}
          >
            {`${currentNumberOfValues} / ${maxValuesPerCustomField}`}
          </span>
        </>
      </FormField>
      <div className="CustomFieldCreationModal__values__callouts">
        {valuesErrors.tooManyCustomFieldValues && (
          <Callout
            className="CustomFieldCreationModal__values__callout"
            variant="alert"
            title={t('customFields.creation.tooManyValuesCallout', {
              max: maxValuesPerCustomField,
              current: currentNumberOfValues,
            })}
          />
        )}
        {valuesErrors.tooManyCharacters && (
          <Callout
            className="CustomFieldCreationModal__values__callout"
            variant="alert"
            title={t('customFields.creation.tooManyCharactersCallout', {
              max: MAX_CHAR_PER_CUSTOM_FIELDS,
            })}
          />
        )}
        {valuesErrors.oneCustomFieldValueRequired && (
          <Callout
            className="CustomFieldCreationModal__values__callout"
            variant="alert"
            title={t('customFields.creation.notEnoughValues')}
          />
        )}
      </div>
    </div>
  );
};
